import { Link, useParams } from 'react-router-dom';
import { getActorById, getDramasByActor } from '@/lib/data';
import { usePageMeta } from '@/hooks/usePageMeta';
import DramaCard from '@/components/feature/DramaCard';

export default function Actor() {
  const { id } = useParams();
  const actor = getActorById(id);

  usePageMeta(
    actor ? `${actor.name} - 演员资料与出演韩剧 - 韩剧` : '演员未找到 - 韩剧',
    actor ? `${actor.name}（${actor.nameEn}）个人资料与代表作品介绍，代表作包括${actor.representative.join('、')}。` : undefined,
  );

  if (!actor) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-24 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
          <i className="ri-user-unfollow-line text-2xl"></i>
        </div>
        <h1 className="mt-4 text-xl font-semibold text-foreground-900">没有找到该演员资料</h1>
        <p className="mt-2 text-sm text-foreground-500">他/她的资料可能尚未收录。</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-1 cursor-pointer whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors">
          <i className="ri-home-4-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  const works = getDramasByActor(actor.id);

  const infoRows = [
    { label: '姓名', value: actor.name },
    { label: '英文名', value: actor.nameEn },
    { label: '出生日期', value: actor.birthday },
    { label: '身高', value: actor.height },
    { label: '经纪公司', value: actor.agency },
  ];

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>演员</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{actor.name}</span>
          </nav>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
          <div className="w-48 md:w-64 shrink-0 rounded-lg overflow-hidden border border-background-200 bg-background-200">
            <div className="w-full aspect-[3/4]">
              <img src={actor.photo} alt={`${actor.name} 演员`} title={`${actor.name} - 韩剧演员`} className="w-full h-full object-cover object-top" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950">{actor.name}</h1>
            <p className="mt-1.5 text-sm text-foreground-500 tracking-wide">{actor.nameEn}</p>

            <div className="mt-4 flex flex-wrap gap-2">
              {actor.representative.map((r) => (
                <span key={r} className="text-xs text-primary-700 bg-primary-100 border border-primary-200 rounded-full px-3 py-1">{r}</span>
              ))}
            </div>

            <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed">{actor.bio}</p>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 text-sm border-b border-background-200 pb-2">
                  <dt className="text-foreground-500 w-20 shrink-0">{row.label}</dt>
                  <dd className="text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <section className="mt-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-heading font-bold text-2xl text-foreground-950">主要出演剧集</h2>
              <p className="mt-2 text-sm text-foreground-500">点击卡片查看剧集详情与剧情介绍</p>
            </div>
            <span className="text-sm text-foreground-500">共 {works.length} 部</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
            {works.map((d) => (
              <DramaCard key={d.id} drama={d} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}